// TODO: Why are we defining all these types in this file?
// Move to the .types.ts file?
export interface ValidationRecord {
  error: string;
}

interface AppNodeValidationRecord {
  auth: { error: string };
  integration: { action: { missing: string } };
  missingLabels?: string;
  missingRecipient?: string;
}

export interface ValidationAiBlockNode {
  minCategories?: { error: string };
  dynamicInput?: { error: string };
  context?: { error: string };
}

export interface ValidationWaitNode {
  notEmpty: { error: string };
}

export interface Nodes {
  nodes: {
    app: AppNodeValidationRecord;
    classifyAiBlock: ValidationAiBlockNode;
    summarizeAiBlock: ValidationAiBlockNode;
    extractAiBlock: ValidationAiBlockNode;
    customAiBlock: ValidationAiBlockNode;
    generateAiBlock: ValidationAiBlockNode;
    wait: ValidationWaitNode;
  };
}

export type NodeKeys = keyof Nodes['nodes'];

export interface CopyType {
  onboarding: { startTrigger: string };
  overview: { createFlow: string };
  activate: { disabled: string };
  create: { error: string };
  ctas: {
    publish: { disabled: string; start: string };
    test: { disabled: string; start: string };
  };
  update: { error: string };
  delete: {
    error: string;
    confirm: {
      title: string;
      description: string;
    };
  };
  publish: {
    start: string;
    error: string;
    success: string;
  };
  test: {
    start: string;
    error: string;
    success: string;
    warning: string;
  };
  node: {
    linkConnection: { error: string };
    updateMetadata: { error: string };
    delete: {
      title: string;
      confirm: string;
      cancel: string;
      error: string;
    };
    path: {
      delete: {
        success: string;
        section?: string;
        step?: string;
        withChildren: {
          title: string;
          confirm: string;
          description: string;
          cancel: string;
          info: string;
        };
      };
    };
    pathNode: {
      empty: string;
    };
  };
  duplicate: {
    loading: string;
    error: string;
  };
  connections: {
    delete: {
      confirm: (usageCount: number) => {
        title: string;
        description: string;
      };
      start: string;
      success: string;
      error: string;
    };
  };
  templates: {
    clone: {
      loading: string;
      error: string;
    };
  };
  validation: Nodes;
  sideMenu: {
    aiBlocks: {
      create: {
        loading: string;
        error: string;
      };
    };
  };
  appMenu?: {
    appSettings: {
      dynamicInput: {
        label: string;
        tooltip: {
          description: string;
        };
      };
    };
  };
}

export const copy: CopyType = {
  onboarding: { startTrigger: 'Start with an action to trigger your flow' },
  overview: { createFlow: 'New AI Flow' },
  activate: {
    disabled: 'You need to publish the flow before activating it.',
  },
  ctas: {
    publish: {
      disabled: 'Please complete all missing flow steps before publishing.',
      start: 'Please wait until your flow is published...',
    },
    test: {
      disabled: 'Please complete all missing flow steps before testing.',
      start: 'Please wait until your flow is tested...',
    },
  },
  create: {
    error: "Flow couldn't be created. Please, try again.",
  },
  update: { error: "Flow couldn't be updated. Please, try again." },
  delete: {
    error: "Flow couldn't be deleted. Please, try again.",
    confirm: {
      title: 'Are you sure you want to delete this flow?',
      description: 'All the history will be lost and the operation is irreversible.',
    },
  },
  publish: {
    start: 'Your flow is being published...',
    error: "Flow couldn't be published. Please, try again.",
    success: 'Your flow was published successfully!',
  },
  test: {
    start: 'Testing flow...',
    error: 'There was an error testing the flow. Contact support if the issue persist.',
    success: 'Your flow was tested successfully!',
    warning: 'The flow was executed, but no output was generated.',
  },
  node: {
    delete: {
      title: 'Do you want to delete this action?',
      confirm: 'Yes, delete',
      cancel: 'Cancel',
      error: 'Node could not be deleted. Please, try again.',
    },
    linkConnection: {
      error: "Authentication couldn't be established. Please, try again.",
    },
    updateMetadata: {
      error: "Node couldn't be updated. Please, try again.",
    },
    path: {
      delete: {
        success: 'Path deleted',
        section: 'Delete entire section',
        step: 'Delete this Step',
        withChildren: {
          title: 'You are about to delete a Step that is connected with more Actions',
          description: 'This can cause your flow to stop running.',
          info: 'Do you want to proceed?',
          confirm: 'Delete',
          cancel: 'Cancel',
        },
      },
    },
    pathNode: {
      empty: 'Follow this path if...',
    },
  },
  duplicate: {
    loading: 'Duplicating your flow...',
    error: 'Flow could not be duplicated. Please, try again or contact support.',
  },
  connections: {
    delete: {
      confirm: (usageCount: number) => ({
        title: 'Are you sure you want to delete this connection?',
        description: `It is being ${connectionUsageCountText(
          usageCount,
        ).toLowerCase()}. All flows using this connection will be deactivated.`,
      }),
      start: 'Removing connection...',
      success: 'Connection removed successfully!',
      error: 'Connection could not be removed. Please, try again.',
    },
  },
  templates: {
    clone: {
      loading: 'Preparing your flow...',
      error: 'Template could not be cloned. Please, try again.',
    },
  },
  validation: {
    nodes: {
      app: {
        auth: { error: 'Missing authentication.' },
        integration: {
          action: { missing: 'Please select the action to be performed.' },
        },
        missingLabels: 'Please select at least one mailbox/label.',
        missingRecipient: 'Recipient email address is missing.',
      },
      classifyAiBlock: {
        minCategories: {
          error: 'Classifier needs to have at least 2 categories.',
        },
        dynamicInput: {
          error: 'Classifier needs to have a value for "What to classify".',
        },
      },
      summarizeAiBlock: {
        dynamicInput: {
          error: 'Summarizer needs to have a value for "What to Summarize".',
        },
      },
      generateAiBlock: {
        dynamicInput: {
          error: 'Generator needs to have a value for "What to Generate".',
        },
      },
      extractAiBlock: {
        minCategories: {
          error: 'Extractor needs to have at least one keyword to extract.',
        },
        dynamicInput: {
          error: 'Extractor needs to have a value for "What to Extract".',
        },
      },
      customAiBlock: {
        dynamicInput: {
          error: 'Custom AI needs to have a value for Input',
        },
        context: {
          error: 'Custom AI needs to have a value for Output instructions',
        },
      },
      wait: {
        notEmpty: {
          error: 'Missing input. Please input a time to wait.',
        },
      },
    },
  },
  sideMenu: {
    aiBlocks: {
      create: {
        loading: 'Creating AI Block...',
        error: 'AI Block could not be created. Please, try again.',
      },
    },
  },
};

export const connectionUsageCountText = (usageCount: number) => {
  if (usageCount === 0) {
    return 'Not used in a published Flow';
  }
  if (usageCount === 1) {
    return `Used in ${usageCount} Flow`;
  }
  return `Used in ${usageCount} Flows`;
};
